import { useState } from "react";
import { ApiException } from "../../../../../services/api/ApiException";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { ICostCenter } from "../../../../../@types/costCenter.type";
import {
  addOrUpdateCostCenter,
  deleteCostCenter,
} from "../../../../../services/api/costCenter/costCenterService";
import { useConfirm } from "../../../../../hooks/useConfirm";
import vectorIcon from "../../../../../assets/_VectorIcon_.svg";

import styles from "./styles.module.scss";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

const schema = z.object({
  id: z.string().optional().nullable(),
  name: z
    .string()
    .min(1, "Nome do Centro de Custo é obrigatório")
    .max(100, "Nome do Centro de Custo deve ter no máximo 100 caracteres"),
  code: z
    .string()
    .min(7, "Código é obrigatório e precisa ter pelo menos 7 dígitos"),
  alias: z.string(),
});

interface FormCostCenterProps {
  pageType: "create" | "update" | "seeMore";
  costCenterData?: ICostCenter;
  isSyncActive?: boolean;
}

export const FormCostCenter: React.FC<FormCostCenterProps> = ({
  pageType,
  costCenterData,
  isSyncActive = false,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ICostCenter>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: costCenterData?.id || undefined,
      name: costCenterData?.name || "",
      code: costCenterData?.code || "",
      alias: costCenterData?.alias || "",
    },
  });

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o Centro de Custo?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const navigate = useNavigate();

  const onSubmit = async (data: ICostCenter) => {
    try {
      setLoading(true);
      await addOrUpdateCostCenter(data);
      reset();
      toast.success(
        "Centro de Custo salvo com sucesso!"
      );
      navigate("/configurations/costCenter");
    } catch (error: any) {
      toast.error(
        "Erro ao salvar Centro de Custo"
      );
      new ApiException(error.message || "Erro ao processar a requisição.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        await deleteCostCenter(costCenterData?.id as string);
        toast.success(" Centro de Custo excluído com sucesso!");
        navigate("/configurations/costCenter");
      } catch (error) {
        console.log(error);
        toast.error(
          " erro ao excluir o Centro de Custo! Tente novamente mais tarde."
        );
      }
    }
  };

  return (
    <>
      <div className={styles.back}>
        <Button
          className={styles.button}
          onClick={() => navigate("/configurations/costCenter")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </Button>
      </div>
      <div className={styles.title}>
        <h1>
          {pageType === "update"
            ? `${costCenterData?.name}`
            : "Novo Centro de Custo"}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.formCostCenter}>
        <div className={styles.fields}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                label="Nome do Centro de Custo"
                placeholder={
                  pageType === "create"
                    ? "Insira o nome do Centro de Custo aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
                disabled={isSyncActive}
              />
            )}
          />

          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TextField
                label="Código do Centro de Custo"
                placeholder={
                  pageType === "create"
                    ? "Insira o código do Centro de Custo aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // remover não numéricos
                  setValue("code", value);
                }}
                error={!!errors.code}
                helperText={errors.code?.message}
                fullWidth
                disabled={isSyncActive}
              />
            )}
          />

          <Controller
            name="alias"
            control={control}
            render={({ field }) => (
              <TextField
                label="Apelido"
                placeholder={
                  pageType === "create" ? "Insira o apelido aqui..." : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.alias}
                helperText={errors.alias?.message}
                fullWidth
                disabled={isSyncActive}
              />
            )}
          />
        </div>

        <div className={styles.btnContainer}>
          {costCenterData?.id && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDeleteClick}
              className={styles.btnsDeleted}
              disabled={loading || isSyncActive}
            >
              Excluir Centro de Custo
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.btnsSave}
            disabled={loading || isSyncActive}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
